"use no memo";

import type { Ref } from "react";

import {
  ProviderIcon,
  Button,
  ButtonGroup,
  ComboBox,
  Heading,
  type CloudProvider,
  type SelectedKey,
} from "@stacklet/ui";
import { Controller, useForm } from "react-hook-form";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";

import { useAlertContext } from "app/contexts/AlertContext";
import { UpdateCostReportingConfigMutation } from "app/mutations/UpdateCostReportingConfigMutation";

import { SettingsTabsLayout } from "../SettingsTabsLayout";
import { CostSettingsPage } from "./CostSettingsPage";

import type {
  CloudCostType,
  CostSettingsRootQuery,
} from "./__generated__/CostSettingsRootQuery.graphql";
import type { UpdateCostReportingConfigMutation as UpdateCostReportingConfigMutationType } from "app/mutations/__generated__/UpdateCostReportingConfigMutation.graphql";

const CLOUD_PROVIDERS: {
  id: string;
  name: string;
  provider: CloudProvider;
  fieldName: keyof CostSettingsForm;
}[] = [
  { id: "aws", name: "AWS", provider: "AWS", fieldName: "awsCostType" },
  { id: "azure", name: "Azure", provider: "Azure", fieldName: "azureCostType" },
  { id: "gcp", name: "GCP", provider: "GCP", fieldName: "gcpCostType" },
];

/** A selectable cost type shown in the per-cloud combobox */
interface CostTypeOption extends SelectedKey {
  /** Cost-type value persisted to the API */
  id: CloudCostType;
  /** Human-readable label shown in the dropdown */
  name: string;
}

const COST_TYPE_OPTIONS: CostTypeOption[] = [
  { id: "BilledCost", name: "Billed Cost" },
  { id: "ListCost", name: "List Cost" },
  { id: "EffectiveCost", name: "Effective Cost" },
];

// Azure does not support Effective Cost
const AZURE_COST_TYPE_OPTIONS = COST_TYPE_OPTIONS.filter(
  (option) => option.id !== "EffectiveCost",
);

// Form schema
interface CostSettingsForm {
  awsCostType: CloudCostType;
  gcpCostType: CloudCostType;
  azureCostType: CloudCostType;
}

/** Props for the CostTypeComboBox component */
interface Props {
  /** Cost-type options available for this cloud */
  options: CostTypeOption[];
  /** Currently selected cost type */
  value: CloudCostType;
  /** Called with the newly selected cost type */
  onChange: (value: CloudCostType) => void;
  /** Ref forwarded to the underlying input for form focus management */
  inputRef?: Ref<HTMLInputElement>;
}

/** Single-select cost-type combobox bound to a react-hook-form field */
function CostTypeComboBox({ inputRef, onChange, options, value }: Props) {
  const selected = options.find((option) => option.id === value);

  return (
    <ComboBox<CostTypeOption>
      className="w-[12.5rem]"
      inputRef={inputRef}
      isClearable={false}
      items={options}
      label="Cost Type"
      selectedItems={{
        items: selected ? [selected] : [],
        append: (item) => onChange(item.id),
      }}
      selectionMode="single"
      tag={(item) => <ComboBox.Tag>{item.name}</ComboBox.Tag>}
    >
      {(item) => (
        <ComboBox.Option id={item.id} textValue={item.name}>
          <span>{item.name}</span>
        </ComboBox.Option>
      )}
    </ComboBox>
  );
}

export function CostSettingsRoot() {
  const { alertDispatch } = useAlertContext();

  const data = useLazyLoadQuery<CostSettingsRootQuery>(
    graphql`
      query CostSettingsRootQuery {
        ...CostSettingsPage_query
        platform {
          costReportingConfig {
            awsCostType
            azureCostType
            gcpCostType
            id
          }
        }
      }
    `,
    {},
  );

  const costConfig = data.platform?.costReportingConfig;

  const { control, handleSubmit, formState, reset } = useForm<CostSettingsForm>(
    {
      values: {
        awsCostType: costConfig?.awsCostType ?? "EffectiveCost",
        gcpCostType: costConfig?.gcpCostType ?? "EffectiveCost",
        azureCostType: costConfig?.azureCostType ?? "BilledCost",
      },
    },
  );

  const [updateCostReportingConfig] =
    useMutation<UpdateCostReportingConfigMutationType>(
      UpdateCostReportingConfigMutation,
    );

  const { isDirty } = formState;

  const onSubmit = (formData: CostSettingsForm) => {
    updateCostReportingConfig({
      variables: {
        input: formData,
      },
      onCompleted: () => {
        alertDispatch({
          message: "Cost settings have been updated successfully!",
          severity: "success",
        });
      },
      onError: (error) => {
        console.error("Failed to update cost settings:", error);
        alertDispatch({
          message: "Failed to update cost settings. Please try again.",
          severity: "error",
        });
      },
    });
  };

  return (
    <CostSettingsPage queryRef={data}>
      <SettingsTabsLayout>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col items-start gap-y-xl">
            <div className="flex flex-col gap-y-lg">
              <p className="text-body-medium text-text-secondary">
                Choose how Stacklet calculates cloud spend and savings
                opportunities:
              </p>
              <ul className="list-disc space-y-sm pl-xl text-body-medium text-text-secondary marker:text-text-secondary">
                <li>
                  <strong>Effective Cost</strong> (AWS/GCP: Net Amortized Cost)
                  - The most comprehensive cost representation and the default
                  cost type for AWS and GCP. It includes all reduced rates and
                  discounts. It additionally includes amortization of reserved
                  instances, savings plans, and other commitments. Distributes
                  upfront costs proportionally across time and usage.{" "}
                  <em>Not currently available for Azure</em>.
                </li>
                <li>
                  <strong>Billed Cost</strong> (AWS: Net Unblended Cost) -
                  Actual amount charged after discounts and credits are applied.
                  Does not include amortization of one-time or recurring
                  purchases.
                </li>
                <li>
                  <strong>List Cost</strong> (AWS: Unblended Cost) - Public
                  on-demand pricing without any discounts applied. The raw cost
                  of resources as reported by the cloud provider.
                </li>
              </ul>
            </div>

            <div className="flex flex-col gap-y-xl">
              <Heading level={4}>Preferred Cost Per Cloud</Heading>
              <div className="flex flex-col gap-y-lg">
                {CLOUD_PROVIDERS.map((provider) => (
                  <div
                    className="flex items-stretch gap-x-2xl"
                    key={provider.id}
                  >
                    {/*
                      Empty label-height spacer keeps the provider icon centered
                      against the combobox input, which sits below its own label.
                    */}
                    <div className="flex shrink-0 flex-col gap-3xs">
                      <span className="invisible text-label-small" aria-hidden>
                        &nbsp;
                      </span>
                      <div className="flex flex-1 items-center">
                        <ProviderIcon provider={provider.provider} />
                      </div>
                    </div>
                    <Controller
                      control={control}
                      name={provider.fieldName}
                      render={({ field }) => (
                        <CostTypeComboBox
                          inputRef={field.ref}
                          onChange={field.onChange}
                          options={
                            provider.id === "azure"
                              ? AZURE_COST_TYPE_OPTIONS
                              : COST_TYPE_OPTIONS
                          }
                          value={field.value}
                        />
                      )}
                    />
                  </div>
                ))}
              </div>
            </div>

            <ButtonGroup>
              <Button
                isDisabled={!isDirty}
                onPress={() => reset()}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button isDisabled={!isDirty} type="submit" variant="primary">
                Save
              </Button>
            </ButtonGroup>
          </div>
        </form>
      </SettingsTabsLayout>
    </CostSettingsPage>
  );
}
